import { ChangeEventHandler, CSSProperties, useEffect, ReactNode } from 'react'
import Select from '../../../../Components/Form/Inputs/Select'
import './TimePicker.less'
import {
  TimeOption,
  TimeOptionInput,
  timeStringToTimeOption,
  TIME_OPTIONS,
} from './TimeStamps/timeStamps'
import { TimeFormat, formatTimeOption } from '../Utilities/formatTimeOption'
import { ClockIcon } from '@/Components/UI/Icons'

interface IsAfterBaseTimeProps {
  baseTime: TimeOption
  selectedTime: TimeOption
  selectableRange: TimeOption[]
}
const isSelectedTimeBeforeBase = ({
  baseTime,
  selectedTime,
  selectableRange,
}: IsAfterBaseTimeProps): boolean => {
  const baseIndex = selectableRange.findIndex((ts) => ts === baseTime)

  const selectedIndex = selectableRange.findIndex((ts) => ts === selectedTime)

  return selectedIndex <= baseIndex
}

const getTimeRange = (start: TimeOption, end: TimeOption) => {
  const timeStamps: TimeOption[] = []
  const startIndex = TIME_OPTIONS.findIndex((ts) => start === ts)

  for (let i = 0; i < 48; i++) {
    const timeStampIndex = (startIndex + i) % 48
    const timeStamp = TIME_OPTIONS[timeStampIndex]
    timeStamps.push(timeStamp)

    if (timeStamp === end) return timeStamps
  }
  return timeStamps
}

interface TimePickerProps extends Partial<Omit<HTMLSelectElement, 'value' | 'style'>> {
  value: TimeOptionInput
  setValue: (val: TimeOptionInput) => void
  format?: TimeFormat
  baseValue?: TimeOption
  rangeStart?: TimeOption
  rangeEnd?: TimeOption
  placeholder?: string
  className?: string
  style?: CSSProperties
  compact?: boolean
  icon?: ReactNode
}

const TimePicker = ({
  id,
  value,
  setValue,
  required,
  baseValue,
  format = '24-hour',
  rangeStart = '00:00:00',
  rangeEnd = '23:30:00',
  placeholder = 'Select a time',
  className,
  style,
  compact,
  icon = <ClockIcon className='clock-icon' aria-hidden='true' />,
}: TimePickerProps) => {
  const onChange: ChangeEventHandler<HTMLSelectElement> = ({ target }) =>
    setValue(timeStringToTimeOption(target.value) ?? '')
  const timeStamps = getTimeRange(rangeStart, rangeEnd)

  const selectableTimes =
    baseValue === undefined ? timeStamps : timeStamps.slice(timeStamps.indexOf(baseValue) + 1)

  useEffect(() => {
    if (value === '') return

    const selectedTime = timeStringToTimeOption(value)

    if (selectedTime === undefined || baseValue === undefined) return

    if (
      isSelectedTimeBeforeBase({ baseTime: baseValue, selectedTime, selectableRange: timeStamps })
    ) {
      setValue(selectableTimes[0] ?? '')
    }
  }, [value, baseValue])

  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      required={required}
      className={className}
      style={style}
      compact={compact}
      icon={icon}>
      <option value='' className='placeholder' disabled>
        {placeholder}
      </option>
      {selectableTimes.map((timeStamp) => (
        <option key={timeStamp} value={timeStamp}>
          {formatTimeOption(timeStamp, format)}
        </option>
      ))}
    </Select>
  )
}

export default TimePicker
